/**
 * Mobile sticky live preview helpers for the patch designer.
 *
 * Keeps the product media column pinned while controls scroll underneath,
 * measures Hat Lab progress offset, and compacts the preview when the
 * on-screen keyboard or a short visual viewport leaves too little room.
 */

const MOBILE_QUERY = '(max-width: 749px)';
const SHORT_VIEWPORT_PX = 480;
const KEYBOARD_FOCUS_SELECTOR =
  'input:not([type="hidden"]):not([type="range"]):not([type="file"]):not([type="checkbox"]):not([type="radio"]), textarea, select';

/**
 * @param {HTMLElement} designer
 * @returns {HTMLElement | null}
 */
function findProductGrid(designer) {
  return designer.closest('.product-information__grid');
}

/**
 * @param {HTMLElement} grid
 * @returns {HTMLElement | null}
 */
function findMedia(grid) {
  return grid.querySelector('.product-information__media');
}

/**
 * @param {HTMLElement} media
 * @returns {HTMLElement}
 */
function ensureSentinel(media) {
  const existing = media.previousElementSibling;

  if (existing instanceof HTMLElement && existing.classList.contains('live-preview-sticky-sentinel')) {
    return existing;
  }

  const sentinel = document.createElement('div');
  sentinel.className = 'live-preview-sticky-sentinel';
  sentinel.setAttribute('aria-hidden', 'true');
  media.parentElement?.insertBefore(sentinel, media);
  return sentinel;
}

/**
 * @param {HTMLElement} grid
 * @returns {() => void}
 */
function observeHatLabProgress(grid) {
  // Preview pins under the site header only. Reserving permanent space for the
  // Hat Lab progress bar left a gap once that bar unstuck, and controls painted
  // through it. Progress keeps its own higher sticky z-index above the preview.
  grid.style.setProperty('--hatlab-progress-sticky-space', '0px');
  return () => {
    grid.style.removeProperty('--hatlab-progress-sticky-space');
  };
}

/**
 * @param {HTMLElement} media
 * @param {HTMLElement} sentinel
 * @returns {() => void}
 */
function observeStuckState(media, sentinel) {
  if (!('IntersectionObserver' in window)) {
    return () => {};
  }

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry) return;

      const stuck = !entry.isIntersecting;
      media.dataset.livePreviewStuck = stuck ? 'true' : 'false';
    },
    {
      threshold: [0, 1],
      rootMargin: '0px 0px 0px 0px',
    }
  );

  observer.observe(sentinel);

  return () => observer.disconnect();
}

/**
 * @param {HTMLElement} media
 * @param {HTMLElement} details
 * @returns {() => void}
 */
function observeKeyboardAndViewport(media, details) {
  /** @type {Element | null} */
  let focusedField = null;

  const sync = () => {
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const shortViewport = viewportHeight > 0 && viewportHeight < SHORT_VIEWPORT_PX;
    const keyboardOpen = focusedField != null;

    media.dataset.livePreviewKeyboard = keyboardOpen ? 'true' : 'false';
    media.dataset.livePreviewShortViewport = shortViewport ? 'true' : 'false';
  };

  /**
   * @param {Event} event
   */
  const onFocusIn = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.matches(KEYBOARD_FOCUS_SELECTOR)) return;

    focusedField = target;
    sync();
  };

  /**
   * @param {Event} event
   */
  const onFocusOut = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.matches(KEYBOARD_FOCUS_SELECTOR)) return;

    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active instanceof Element && details.contains(active) && active.matches(KEYBOARD_FOCUS_SELECTOR)) {
        focusedField = active;
      } else {
        focusedField = null;
      }
      sync();
    });
  };

  details.addEventListener('focusin', onFocusIn);
  details.addEventListener('focusout', onFocusOut);

  const visualViewport = window.visualViewport;
  visualViewport?.addEventListener('resize', sync);
  visualViewport?.addEventListener('scroll', sync);
  window.addEventListener('resize', sync, { passive: true });
  sync();

  return () => {
    details.removeEventListener('focusin', onFocusIn);
    details.removeEventListener('focusout', onFocusOut);
    visualViewport?.removeEventListener('resize', sync);
    visualViewport?.removeEventListener('scroll', sync);
    window.removeEventListener('resize', sync);
  };
}

/**
 * @param {HTMLElement} designer
 * @returns {() => void}
 */
function bindDesigner(designer) {
  const grid = findProductGrid(designer);
  if (!grid) return () => {};

  const media = findMedia(grid);
  if (!media) return () => {};

  const details = grid.querySelector('.product-details');
  if (!(details instanceof HTMLElement)) return () => {};

  media.classList.add('live-preview-sticky-media');
  const sentinel = ensureSentinel(media);

  const cleanups = [
    observeHatLabProgress(grid),
    observeStuckState(media, sentinel),
    observeKeyboardAndViewport(media, details),
  ];

  return () => {
    for (const cleanup of cleanups) cleanup();
    media.classList.remove('live-preview-sticky-media');
    delete media.dataset.livePreviewStuck;
    delete media.dataset.livePreviewKeyboard;
    delete media.dataset.livePreviewShortViewport;
    grid.style.removeProperty('--hatlab-progress-sticky-space');
  };
}

function init() {
  /** @type {Map<HTMLElement, () => void>} */
  const bindings = new Map();

  const syncAll = () => {
    const designers = document.querySelectorAll('patch-designer');

    for (const designer of designers) {
      if (!(designer instanceof HTMLElement)) continue;
      if (bindings.has(designer)) continue;
      bindings.set(designer, bindDesigner(designer));
    }

    for (const [designer, cleanup] of bindings) {
      if (document.contains(designer)) continue;
      cleanup();
      bindings.delete(designer);
    }
  };

  syncAll();

  document.addEventListener('shopify:section:load', syncAll);
  document.addEventListener('shopify:section:unload', syncAll);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
